/** Play/pause when the player is near or in the viewport */
const AUTOPLAY_OBSERVER_OPTIONS = {
  rootMargin: '100px 0px',
  threshold: 0.1,
};

const NEAR_VIEWPORT_PX = 100;

/**
 * @param {() => void} fn
 * @param {number} [wait=150]
 */
function debounce(fn, wait = 150) {
  let timer = 0;
  const debounced = () => {
    clearTimeout(timer);
    timer = window.setTimeout(fn, wait);
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}

/** @param {() => void} fn */
function afterLoad(fn) {
  if (document.readyState === 'complete') {
    fn();
    return;
  }
  window.addEventListener('load', fn, { once: true });
}

class VideoPlayer extends HTMLElement {
  #video = null;
  #videoOverlay = null;
  #playButton = null;
  #soundButton = null;
  #template = null;
  #externalPosters = [];
  #lazy = false;
  #autoplay = false;
  #deferMedia = false;
  #intersectionObserver = null;
  #userInteracted = false;
  /** @type {ReturnType<typeof debounce> | null} */
  #onBreakpointSync = null;
  /** @type {AbortController | null} */
  #uiAbort = null;
  /** @type {AbortController | null} */
  #videoAbort = null;

  connectedCallback() {
    this.#videoOverlay = this.parentElement?.querySelector('[data-video-overlay]');
    this.#playButton = this.querySelector('[data-action="play-pause-video"]');
    this.#soundButton = this.querySelector('[data-action="sound-toggle"]');
    this.#template = this.querySelector('template[data-video-template]');
    this.#externalPosters = [
      ...this.querySelectorAll('[data-video-poster]'),
      ...(this.parentElement?.querySelectorAll('[data-hero-video-poster]') ?? []),
    ];
    this.#lazy = this.hasAttribute('data-lazy');
    this.#autoplay = this.hasAttribute('data-autoplay');
    this.#deferMedia = this.hasAttribute('data-defer-media');
    this.#video = this.querySelector('video');

    if (this.#deferMedia && this.#template) {
      this.#setupDeferred();
      return;
    }

    if (!this.#video) return;
    this.#initImmediate();
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#onBreakpointSync?.cancel();
    window.removeEventListener('resize', this.#onBreakpointSync);
    this.#uiAbort?.abort();
    this.#videoAbort?.abort();
  }

  #setupDeferred() {
    this.#handleReducedMotion();
    this.#bindUi();

    afterLoad(() => {
      // Observe even if breakpoint-hidden; resize sync covers becoming visible.
      this.#observe(this, this.#onViewportChange);
      this.#syncDeferredPlayback();
    });

    this.#onBreakpointSync = debounce(() => this.#syncDeferredPlayback());
    window.addEventListener('resize', this.#onBreakpointSync, { passive: true });
  }

  #initImmediate() {
    this.#handleReducedMotion();
    this.#bindUi();

    if (!this.#autoplay && !this.#lazy) return;

    if (this.#autoplay) this.#video.pause();
    this.#observe(this.#video, this.#onViewportChange);
  }

  /**
   * After lg/orientation changes, a previously hidden player may become visible
   * without a new IO callback — inject/play or pause accordingly.
   */
  #syncDeferredPlayback() {
    if (!this.#deferMedia) return;
    this.#applyViewportPlayback(this.#isNearViewport());
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onViewportChange = (entries) => {
    for (const entry of entries) {
      this.#applyViewportPlayback(entry.isIntersecting);
    }
  };

  /** @param {boolean} inView */
  #applyViewportPlayback(inView) {
    if (!this.#isDisplayed() || !inView) {
      this.#pausePlayback();
      return;
    }
    if (!this.#wantsPlayback()) return;
    this.#injectMedia();
    this.#playWhenReady();
  }

  #wantsPlayback() {
    return this.#autoplay || this.#userInteracted;
  }

  #isNearViewport() {
    const rect = this.getBoundingClientRect();
    return rect.bottom > -NEAR_VIEWPORT_PX && rect.top < window.innerHeight + NEAR_VIEWPORT_PX;
  }

  /**
   * @param {Element} target
   * @param {IntersectionObserverCallback} callback
   */
  #observe(target, callback) {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = new IntersectionObserver(callback, AUTOPLAY_OBSERVER_OPTIONS);
    this.#intersectionObserver.observe(target);
  }

  #injectMedia() {
    if (this.#video || !this.#template) return;

    const node = this.#template.content.cloneNode(true);
    this.#template.replaceWith(node);
    this.#video = this.querySelector('video');
    if (!this.#video) return;

    this.#video.classList.add('absolute', 'inset-0', 'size-full', 'object-cover');
    this.#bindVideoEvents();
  }

  #bindUi() {
    this.#uiAbort?.abort();
    this.#uiAbort = new AbortController();
    const { signal } = this.#uiAbort;

    this.addEventListener('click', this.#handleHostClick, { signal });
    this.#videoOverlay?.addEventListener('click', this.#handleClick, { signal });
    this.#playButton?.addEventListener('click', this.#handlePlayButtonClick, { signal });
    this.#soundButton?.addEventListener('click', this.#handleSoundClick, { signal });
    this.#bindVideoEvents();
  }

  #bindVideoEvents() {
    if (!this.#video) return;

    this.#videoAbort?.abort();
    this.#videoAbort = new AbortController();
    const { signal } = this.#videoAbort;

    this.#video.addEventListener('click', this.#handleClick, { signal });
    this.#video.addEventListener('play', this.#toggleButton, { signal });
    this.#video.addEventListener('pause', this.#toggleButton, { signal });
    this.#video.addEventListener('playing', this.#onVideoPlaying, { once: true, signal });
  }

  #playWhenReady() {
    if (!this.#video) return;
    this.#video.play()?.catch((error) => {
      console.warn('[video-player] play() failed', error);
    });
    this.#toggleButton();
  }

  #pausePlayback() {
    this.#video?.pause();
    this.#toggleButton();
  }

  #onVideoPlaying = () => {
    this.dataset.ready = 'true';
    this.classList.remove('opacity-0');
    this.classList.add('opacity-100');
    if (!this.#isDisplayed()) return;

    for (const poster of this.#externalPosters) {
      if (poster.getClientRects().length === 0) continue;
      poster.classList.add('invisible');
      poster.setAttribute('aria-hidden', 'true');
    }
  };

  #isDisplayed() {
    return this.getClientRects().length > 0;
  }

  #handleHostClick = (event) => {
    if (event.target.closest('[data-action]')) return;
    this.#handleClick(event);
  };

  #handlePlayButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#togglePlayback();
  };

  #disableAutoplay() {
    this.#autoplay = false;
    this.#pausePlayback();
  }

  #handleReducedMotion() {
    if (!this.#autoplay) return;
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#disableAutoplay();
  }

  #shouldIgnoreClick(event) {
    return !this.#isDisplayed() || Boolean(event?.target?.closest?.('a'));
  }

  #togglePlayback() {
    this.#injectMedia();
    if (!this.#video) return;

    this.#userInteracted = true;
    if (this.#video.paused) {
      this.#playWhenReady();
    } else {
      this.#pausePlayback();
    }
  }

  #handleClick = (event) => {
    if (this.#shouldIgnoreClick(event)) return;
    event?.stopPropagation?.();
    this.#togglePlayback();
  };

  #handleSoundClick = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.#injectMedia();
    if (!this.#video) return;

    this.#video.muted = !this.#video.muted;
    this.#toggleSoundButton();
  };

  play() {
    this.#injectMedia();
    this.#userInteracted = true;
    this.#playWhenReady();
  }

  pause() {
    this.#pausePlayback();
  }

  #toggleButton = () => {
    if (!this.#video || !this.#playButton) return;
    const playIcon = this.#playButton.querySelector('[data-icon="regular-play"]');
    const pauseIcon = this.#playButton.querySelector('[data-icon="regular-pause"]');
    playIcon?.classList.toggle('hidden', !this.#video.paused);
    pauseIcon?.classList.toggle('hidden', this.#video.paused);
  };

  #toggleSoundButton() {
    if (!this.#video) return;
    const soundIcon = this.#soundButton?.querySelector('[data-icon="regular-volume-on"]');
    const muteIcon = this.#soundButton?.querySelector('[data-icon="regular-volume-off"]');
    soundIcon?.classList.toggle('hidden', this.#video.muted);
    muteIcon?.classList.toggle('hidden', !this.#video.muted);
  }
}

customElements.define('video-player', VideoPlayer);
